// src/b2b/components/ChildrenDOBModal.jsx
import React, { useEffect, useMemo, useState } from 'react';

/* ------------------ Utils ------------------ */
const range = (a, b) => {
  const start = Math.min(a, b);
  const end = Math.max(a, b);
  const out = [];
  for (let i = start; i <= end; i++) out.push(i);
  return out;
};


const parseYMD = (y, m, d) => new Date(Number(y), Number(m) - 1, Number(d));

const ageOn = (dob, ref) => {
  let age = ref.getFullYear() - dob.getFullYear();
  const m = ref.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < dob.getDate())) age--;
  return age;
};

const MONTHS = [
  { v: 1,  label: '01' }, { v: 2,  label: '02' }, { v: 3,  label: '03' },
  { v: 4,  label: '04' }, { v: 5,  label: '05' }, { v: 6,  label: '06' },
  { v: 7,  label: '07' }, { v: 8,  label: '08' }, { v: 9,  label: '09' },
  { v: 10, label: '10' }, { v: 11, label: '11' }, { v: 12, label: '12' },
];

/**
 * Props:
 * - isOpen
 * - onClose
 * - count               : nombre d’enfants
 * - departDate          : "YYYY-MM-DD" (optionnel)
 * - endDate             : "YYYY-MM-DD" (optionnel)  // ✅ NEW: bornage à la fin du séjour
 * - onApply({ childrenCount, childrenDOB })
 * - initialDOB          : [{day, month, year, ageOnDeparture?, isFree?}]  <-- ✅ nouveau
 */
export default function ChildrenDOBModal({
  isOpen,
  onClose,
  count = 0,
  departDate,
  endDate,
  onApply,
  initialDOB = [],
  invalidIdx = [],    
}) {

  const isBad = (i) => (invalidIdx || []).includes(i);
  // État interne des champs (pré-rempli depuis initialDOB)
  const [children, setChildren] = useState([]);
  const [errors, setErrors] = useState([]);

  // Années autorisées : 0..11 ans (12 années)
  const thisYear = new Date().getFullYear();
  const years = useMemo(() => range(thisYear - 12, thisYear).reverse(), [thisYear]);
  const days  = useMemo(() => range(1, 31).map((d) => String(d).padStart(2, '0')), []);

  // À l’ouverture du modal, on initialise depuis initialDOB
  useEffect(() => {
    if (!isOpen) return;
    setChildren((prev) => {
      // on part de ce qu'on a reçu en props
      const seeded = Array.isArray(initialDOB) ? initialDOB.map((c) => ({
        day: String(c?.day || '').padStart(2, '0'),
        month: String(c?.month || '').padStart(2, '0'),
        year: String(c?.year || ''),
      })) : [];

      // ajuste la longueur au "count" demandé
      if (seeded.length < count) {
        for (let i = seeded.length; i < count; i++) {
          seeded.push({ day: '', month: '', year: '' });
        }
      } else if (seeded.length > count) {
        seeded.length = count;
      }
      return seeded;
    });
    setErrors((e) => {
      const n = Array.isArray(e) ? [...e] : [];
      n.length = count;
      return n;
    });
  }, [isOpen, count, initialDOB]);

  if (!isOpen) return null;

  const refDate = endDate ? new Date(endDate) : (departDate ? new Date(departDate) : new Date());

  const update = (i, patch) =>
    setChildren((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  const handleApply = () => {
    const errs = [];
    const out = [];

    for (let i = 0; i < count; i++) {
      const c = children[i] || {};
      const { day, month, year } = c;

      // requis
      if (!year || !month || !day) {
        errs[i] = 'Date de naissance requise';
        continue;
      }

      const dob = parseYMD(year, month, day);

      // invalides / futur
      if (Number.isNaN(dob.getTime())) {
        errs[i] = 'Date invalide';
        continue;
      }
      if (dob > refDate) {
        errs[i] = 'La naissance ne peut pas être dans le futur';
        continue;
      }

      // âge strict à la date de départ (doit être < 12)
      const age = ageOn(dob, refDate); // refDate = fin du séjour si fournie
      if (age >= 12) {
        errs[i] = 'L’enfant doit avoir moins de 12 ans à la date de départ';
        continue;
      }
      if (age < 0) {
        errs[i] = 'Âge invalide';
        continue;
      }

      errs[i] = '';
      out[i] = {
        day,
        month,
        year,
ageOnEnd: age,
        isInfant: age < 2,
        isChild: age >= 2 && age < 12,
      };
    }

    setErrors(errs);
    if (errs.some(Boolean)) return;

    onApply?.({ childrenCount: count, childrenDOB: out });
    onClose?.();
  };

  const fmtFR = (d) => {
    try {
      return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return d; }
  };

  return (
    <div className="modal d-block" role="dialog" style={{ background: 'rgba(0,0,0,.45)' }} aria-modal="true">
      <div className="modal-dialog">
        <div className="modal-content">

          <div className="modal-header">
            <h5 className="modal-title">Dates de naissance des enfant(s)</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>

          <div className="modal-body">
            <p className="text-muted mb-3">
    Âge calculé à la <strong>date de fin du séjour</strong>
    {' '}<strong>{endDate ? fmtFR(endDate) : departDate ? fmtFR(departDate) : '(non définie)'}</strong>.
    Les enfants doivent avoir <strong>moins de 12 ans</strong> à cette date.
  </p>

            {count === 0 && (
              <div className="alert alert-info">Aucun enfant sélectionné.</div>
            )}

            {Array.from({ length: count }).map((_, i) => (
              <div key={i} className="border rounded p-3 mb-3">
                <div className="fw-semibold mb-2">Enfant {i + 1}</div>

                <div className="row g-2">
                  <div className="col-4">
                    <label className="form-label">Jour</label>
                    <select
                       className={`form-control${isBad(i) ? " is-invalid" : ""}`}
                      value={children[i]?.day || ''}
                      onChange={(e) => update(i, { day: e.target.value })}
                    >
                      <option value="">JJ</option>
                      {days.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-4">
                    <label className="form-label">Mois</label>
                    <select
                       className={`form-control${isBad(i) ? " is-invalid" : ""}`}
                      value={children[i]?.month || ''}
                      onChange={(e) => update(i, { month: e.target.value })}
                    >
                      <option value="">MM</option>
                      {MONTHS.map((m) => (
                        <option key={m.v} value={String(m.v).padStart(2,'0')}>{m.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-4">
                    <label className="form-label">Année</label>
                    <select
                       className={`form-control${isBad(i) ? " is-invalid" : ""}`}
                      value={children[i]?.year || ''}
                      onChange={(e) => update(i, { year: e.target.value })}
                    >
                      <option value="">AAAA</option>
                      {years.map((y) => (
                        <option key={y} value={String(y)}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {!!errors[i] && <div className="text-danger small mt-2">{errors[i]}</div>}
              </div>
            ))}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
              Annuler
            </button>
            <button type="button" className="btn btn-primary" onClick={handleApply}>
              Appliquer
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
